import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import { PaymentOptions } from '@/components/PaymentOptions';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login() {
  const navigate = useNavigate();
  const { login, sessionError, clearSessionError } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch {
      toast.error('Error al enviar solicitud');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      navigate('/social');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      if (detail === 'SUBSCRIPTION_EXPIRED' || status === 402) {
        setShowPayment(true);
      } else if (detail === 'SESSION_EXPIRED_ANOTHER_DEVICE') {
        toast.error('Tu sesión se cerró porque te conectaste desde otro dispositivo');
      } else {
        toast.error(detail || 'Error al iniciar sesión');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show payment/renewal screen
  if (showPayment || sessionError === 'SUBSCRIPTION_EXPIRED') {
    return (
      <div className="min-h-screen bg-[#020402] flex items-center justify-center px-4 py-8 grid-overlay">
        <PaymentOptions
          title="SUSCRIPCIÓN EXPIRADA"
          subtitle="Tu período de acceso ha finalizado. Renueva tu suscripción para continuar."
          userName={formData.email?.split('@')[0] || 'Usuario'}
          userEmail={formData.email}
          onBack={() => { setShowPayment(false); clearSessionError(); }}
          mode="renewal"
        />
      </div>
    );
  }

  // Show another device message
  if (sessionError === 'ANOTHER_DEVICE') {
    return (
      <div className="min-h-screen bg-[#020402] flex items-center justify-center px-6 py-12 grid-overlay">
        <div className="w-full max-w-md">
          <div className="glass-panel rounded-lg p-8 alien-glow text-center" data-testid="another-device-screen">
            <AlertTriangle className="h-12 w-12 text-orange-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-emerald-50 mb-3" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
              SESIÓN CERRADA
            </h2>
            <p className="text-emerald-100/70 mb-6">
              Tu cuenta se conectó desde otro dispositivo. Solo puedes tener una sesión activa a la vez.
            </p>
            <Button
              onClick={() => { clearSessionError(); }}
              className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold"
            >
              Iniciar Sesión Nuevamente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020402] flex items-center justify-center px-6 py-12 grid-overlay">
      <div className="w-full max-w-md">
        <div className="glass-panel rounded-lg p-8 alien-glow" data-testid="login-form">
          <div className="flex justify-center mb-8">
            <img src="/logo.png" alt="Trading Alienígena Logo" className="w-16 h-16 object-contain" />
          </div>
          <h2 className="text-3xl font-bold text-center mb-2 text-emerald-50" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
            INICIAR SESIÓN
          </h2>
          <p className="text-center text-emerald-100/60 mb-8">Accede a tu cuenta de Trading Alienígena</p>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-emerald-100">Email</Label>
              <Input
                id="email" type="email" placeholder="tu@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-black/50 border-emerald-500/30 text-emerald-50 focus:border-emerald-500 placeholder:text-emerald-500/30"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-emerald-100">Contraseña</Label>
              <Input
                id="password" type="password" placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="bg-black/50 border-emerald-500/30 text-emerald-50 focus:border-emerald-500 placeholder:text-emerald-500/30"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit" disabled={loading}
              className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold uppercase tracking-wider py-6"
              data-testid="login-submit-btn"
            >
              {loading ? 'Cargando...' : 'Ingresar'}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <button onClick={() => setShowForgot(true)} className="text-emerald-400/70 hover:text-emerald-400 text-sm" data-testid="forgot-password-link">
              ¿Olvidaste tu contraseña?
            </button>
          </div>

          {showForgot && (
            <div className="mt-4 bg-black/30 border border-emerald-500/20 rounded-lg p-4">
              {forgotSent ? (
                <div className="text-center">
                  <p className="text-emerald-400 text-sm mb-2">Enlace enviado</p>
                  <p className="text-emerald-100/60 text-xs">Revisa tu correo para restablecer tu contraseña.</p>
                </div>
              ) : (
                <form onSubmit={handleForgot} className="space-y-3">
                  <Label className="text-emerald-100 text-sm">Ingresa tu email</Label>
                  <Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="tu@email.com" required className="bg-black/50 border-emerald-500/30 text-emerald-50 text-sm" data-testid="forgot-email-input" />
                  <Button type="submit" disabled={loading} className="w-full bg-emerald-500 text-black hover:bg-emerald-400 text-sm" data-testid="forgot-submit-btn">
                    {loading ? 'Enviando...' : 'Enviar enlace'}
                  </Button>
                </form>
              )}
            </div>
          )}

          <div className="mt-4 text-center">
            <p className="text-emerald-100/60">
              ¿No tienes cuenta?{' '}
              <Link to="/register" className="text-emerald-400 hover:text-emerald-300 font-semibold">Haz parte aquí</Link>
            </p>
          </div>
          <div className="mt-4 text-center">
            <Link to="/" className="text-emerald-400/70 hover:text-emerald-400 text-sm">← Volver al inicio</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
